import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';

/**
 * Appearance modes (Clarity v4 app-redesign). The control is a topbar
 * segmented Mixed / Light / Dark, faithful to the design:
 *   - mixed — light page with signature dark hero panels
 *   - light — flat light surfaces
 *   - dark  — dark canvas
 *
 * `data-theme` drives the light/dark token set; `data-mode` carries the
 * three-way choice so the "mixed" hero treatment can key off it.
 *
 * When nothing is stored the default follows the OS (dark → Dark, else Mixed),
 * and stays OS-reactive until the user picks — after which the choice is
 * sticky. This mirrors the redesign's "OS-aware default + sticky override".
 */
export type Theme = 'mixed' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

const STORAGE_KEY = 'fhq-theme';

function osDefault(): Theme {
  if (typeof window === 'undefined' || !window.matchMedia) return 'mixed';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'mixed';
}

let cachedRaw: string | null | undefined;
let cachedStored: Theme | null = null;

// Returns the user's explicit choice, or null when unset/legacy (e.g. the old
// 'system' value) so the caller can fall back to the OS default.
function readStore(): Theme | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === cachedRaw) return cachedStored;
    cachedRaw = raw;
    cachedStored =
      raw === 'light' || raw === 'dark' || raw === 'mixed' ? raw : null;
    return cachedStored;
  } catch {
    return cachedStored;
  }
}

function writeStore(theme: Theme) {
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // ignore — cache still reflects intent
  }
  cachedRaw = theme;
  cachedStored = theme;
  window.dispatchEvent(new StorageEvent('storage', { key: STORAGE_KEY }));
}

function subscribe(callback: () => void) {
  const handler = (e: StorageEvent) => {
    if (e.key === STORAGE_KEY || e.key === null) callback();
  };
  window.addEventListener('storage', handler);
  return () => window.removeEventListener('storage', handler);
}

/**
 * Apply the theme to <html>. Kept in sync with the pre-paint script in
 * index.html (which resolves the same default before first paint, no flash).
 */
function applyTheme(theme: Theme) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.dataset.theme = theme === 'dark' ? 'dark' : 'light';
  root.dataset.mode = theme;
}

export function useTheme() {
  const stored = useSyncExternalStore(subscribe, readStore, () => null);
  const [osPref, setOsPref] = useState<Theme>(osDefault);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => setOsPref(mq.matches ? 'dark' : 'mixed');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const theme: Theme = stored ?? osPref;

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const resolved: ResolvedTheme = theme === 'dark' ? 'dark' : 'light';
  const setTheme = useCallback((next: Theme) => writeStore(next), []);

  return { theme, setTheme, resolved };
}
